import React from 'react';
import styled, { css } from 'styled-components';
import { colors, breakpoint, sizes } from 'variables';
import { percentage } from 'modules/helpers/style';
import type { LiveEventStatus } from 'autogen/sharedEnums';
import type { IResponsiveImageModel } from 'shared/interfaces/responsiveImage';
import { CardLogo, AnimatedCardLogo, AnimatedPlayLogo } from 'components/live/cardIcons';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import type { ConnectedContentProductDisplayModel } from 'autogen/swagger/all';
import { Spacing12 } from 'autogen/design-tokens/tokens';
import ArticleCard from 'components/ArticleCard/ArticleCard';
import Tag from 'components/generic/tag/Tag';
import { firstCharacterToLower } from 'modules/helpers/strings';
import Image from 'components/generic/Image';
import { getLargestPictureSrc } from 'helpers/imageScalingHelper';
import PageAction from 'components/shared/Routing/PageAction';

const Container = styled(PageAction)`
    display: flex;
    flex: none;
    scroll-snap-align: start;
    cursor: pointer;
    min-width: 0;
    text-align: left;
`;

const StyledArticleCard = styled(ArticleCard)`
    flex: 1 1 auto;
    height: 360px;
`;

const ImageContainer = styled.div`
    position: relative;
    overflow: hidden;
    height: 200px;
`;

const StyledTag = styled(Tag)`
    position: absolute;
    top: ${Spacing12};
    left: ${Spacing12};
`;

const StyledImage = styled(Image.WithScaling)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const NoImage = styled.div`
    width: 100%;
    padding-bottom: ${percentage(4, 4)};
    background: ${colors.liveLabel};

    ${Container}:only-child & {
        padding-bottom: ${percentage(9, 21)};
    }
`;

const playButtonSize = css`
    ${breakpoint.up(sizes.xl)} {
        width: 52px;
        height: 52px;
    }
`;

const StyledCardIcon = styled(CardLogo)`
    ${breakpoint.up(sizes.xl)} {
        width: 100px;
        height: 100px;
    }
`;
const StyledAnimatedPlayLogo = styled(AnimatedPlayLogo)`
    ${playButtonSize}
`;
const StyledAnimatedCardLogo = styled(AnimatedCardLogo)`
    ${playButtonSize}
`;

export interface ILiveEventPDPCard {
    name: string;
    image?: IResponsiveImageModel;
    status: LiveEventStatus;
    bambuserShowId: string;
}

const trackClick = (layoutType: ConnectedContentProductDisplayModel['layoutType'], trackingIndex: number): void => {
    if (!window?.dataLayer) return;

    if (layoutType) {
        UAEventTracking({
            eventCategory: 'connectedContent',
            eventAction: firstCharacterToLower(layoutType),
            eventLabel: `item_${trackingIndex}`
        });
    }
};

export interface IConnectedContentProductDisplayModel {
    card: ConnectedContentProductDisplayModel;
    trackingIndex: number;
}

const LiveEventPDPCard = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, IConnectedContentProductDisplayModel>(
    ({ card, trackingIndex }, ref) => {
        const { heading, image, contentLabel, layoutType, dataLayer, link: maybeLink, layoutText, ctaText } = card;
        const { pageAction, liveEventModel } = maybeLink || {};
        const largestImage = getLargestPictureSrc(image);

        const getPlayIcon = (type) => {
            const { status } = liveEventModel || {};

            if (type === 'PdpLive' || type === 'PdpVideo') {
                switch (status) {
                    case 'Live':
                        return <StyledAnimatedPlayLogo />;
                    case 'Upcoming':
                        return <StyledCardIcon sprite="LiveSoonCard" />;
                    default:
                        return <StyledAnimatedCardLogo />;
                }
            }

            return null;
        };

        if (!pageAction) return null;

        return (
            <Container
                action={pageAction}
                onClick={() => trackClick(layoutType, trackingIndex)}
                data-datalayer={JSON.stringify(dataLayer)}
                ref={ref}
            >
                <StyledArticleCard
                    textBox={{
                        title: heading || '',
                        overline: layoutText,
                        action: { buttonText: ctaText }
                    }}
                >
                    <ImageContainer>
                        {layoutType !== 'PdpStories' && getPlayIcon(layoutType)}
                        {image?.fallBackUrl ? (
                            <StyledImage alt="" sizes={250} src={largestImage} transformType="WidthScale" />
                        ) : (
                            <NoImage />
                        )}
                        {contentLabel && <StyledTag>{contentLabel}</StyledTag>}
                    </ImageContainer>
                </StyledArticleCard>
            </Container>
        );
    }
);
export default LiveEventPDPCard;
